import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import gibTypesActions from 'store/features/gib-types/actions';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { CreateGibTypeDialog, UpdateGibTypeDialog } from './components';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { selectGibTypes, selectGibTypesIsEmpty, selectGibTypesLoading } from 'store/features/gib-types/selector';

const GibTypes = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();
  const gibTypes = useAppSelector(selectGibTypes);
  const loading = useAppSelector(selectGibTypesLoading);
  const isEmptyGibTypes = useAppSelector(selectGibTypesIsEmpty);
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [openDeleteGibType, setOpenDeleteGibType] = useState(false);
  const [openCreateGibType, setOpenCreateGibType] = useState(false);
  const [openUpdateGibType, setOpenUpdateGibType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'gibTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(gibTypesActions.getGibTypes());
  };

  // sync helper functions
  const handleCloseDeleteGibType = () => setOpenDeleteGibType(false);
  const handleClickOpenDeleteGibType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteGibType(true);
  };

  const handleCloseCreateGibType = () => setOpenCreateGibType(false);
  const handleClickOpenCreateGibType = () => setOpenCreateGibType(true);

  const handleCloseUpdateGibType = () => setOpenUpdateGibType(false);
  const handleClickOpenUpdateGibType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateGibType(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-gib-method')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!gibTypes.length && !isEmptyGibTypes) {
      dispatch(gibTypesActions.getGibTypes());
    }
  }, [gibTypes, isEmptyGibTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-gib-method']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateGibType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="gibTypes.createGibType" defaultMessage="GİB Yöntemi Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-gib-method']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateGibType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="gibTypes.updateGibType" defaultMessage="GİB Yöntemi Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-gib-method']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteGibType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="gibTypes.deleteGibType" defaultMessage="GİB Yöntemi Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateGibTypeDialog open={openCreateGibType} handleClose={handleCloseCreateGibType} refresh={refresh} />
      <UpdateGibTypeDialog
        open={openUpdateGibType}
        gibTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateGibType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteGibType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteGibType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="gibTypes.deleteGibType" defaultMessage="GİB Yöntemi Sil" />}
        subTitle={
          <FormattedMessage
            id="gibTypes.deleteGibTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra GİB Yöntemi veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteGibType}
        successMessage={
          <FormattedMessage
            id="gibTypes.deleteGibTypeCompleted"
            defaultMessage="Seçili GİB Yöntemini silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={gibTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default GibTypes;
